import {
  Button,
  FlatTree,
  makeStyles,
  TreeOpenChangeData,
  TreeOpenChangeEvent,
} from "@fluentui/react-components";
import {
  ArrowCollapseAll20Regular,
  ArrowExpandAll20Regular,
} from "@fluentui/react-icons";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelectedTag } from "../../contexts/SelectedTagContext";
import { useGetUserAccessLevel } from "../../hooks/useGetUserAccessLevel";
import { FlatVsaaTag } from "../../models/VsaaTag";
import { distinct, getAncestors } from "../../services/TagService";
import { SkanskaColorTokens } from "../../style/colors";
import { TagTreeSkeleton } from "../skeletons/TagTreeSkeleton";
import { CreateTreeItemDialog } from "./CreateTreeItemDialog";
import { ExportTagsDialog } from "./ExportTagsDialog";
import { ImportTagsDialog } from "./ImportTagsDialog";
import { TagOverviewPanel } from "./TagOverviewPanel";
import TagTreeItem from "./TagTreeItem";

const useStyles = makeStyles({
  tagContainer: {
    display: "flex",
    flexDirection: "row",
    borderTop: "1px solid #ccc",
    position: "relative",
  },
  leftTagPanel: {
    width: "550px",
    borderRight: "1px solid #ccc",
    padding: "10px",
  },
  rightTagPanel: {
    flex: 1,
    marginLeft: "20px",
    padding: "10px",
  },
  tree: {
    overflowY: "auto",
    maxHeight: "90vh",
  },
  treeHeader: {
    display: "flex",
    marginBottom: "20px",
  },
  buttonPanel: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    borderBottom: "1px solid #ccc",
    width: "100%",
  },
  treeHeaderButton: {
    height: "33px",
    marginLeft: "8px",
    marginBottom: "5px",
  },
  selectedTag: {
    backgroundColor: SkanskaColorTokens.color_primary_gray,
  },
});

interface TagCollectionTreeProps {
  tags: FlatVsaaTag[];
  allTags: FlatVsaaTag[];
  tagCollectionId: string;
  openItems: string[];
  isLoading: boolean;
  onOpenItemsChange: (newOpenItems: string[]) => void;
}

export const TagCollectionTree = (props: TagCollectionTreeProps) => {
  const classes = useStyles();
  const {
    tags,
    allTags,
    openItems,
    onOpenItemsChange,
    tagCollectionId,
    isLoading,
  } = props;
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [createItemAncestorIds, setCreateItemAncestorIds] = useState<
    string[] | undefined
  >(undefined);
  const [createDialogTitle, setCreateDialogTitle] = useState<string>("");
  const [overviewPanelIsProcessingTags, setOverviewPanelIsProcessingTags] =
    useState<boolean>(false);

  const tagContext = useSelectedTag();
  const accessLevel = useGetUserAccessLevel();
  const { t } = useTranslation();

  const handleSelectedTag = (tag: FlatVsaaTag | undefined) => {
    tagContext?.setSelectedTag(tag ? { ...tag } : undefined);
  };

  const tagIsOpen = useCallback(
    (tagId: string) => {
      return openItems.indexOf(tagId) !== -1;
    },
    [openItems]
  );

  const handleOpenChange = (
    _event: TreeOpenChangeEvent,
    data: TreeOpenChangeData
  ) => {
    let newOpenItems: string[];
    const tagId = data.value as string;

    if (data.open && !tagIsOpen(tagId)) {
      newOpenItems = [...openItems, tagId];
      onOpenItemsChange(newOpenItems);
    } else if (!data.open && tagIsOpen(tagId)) {
      newOpenItems = openItems.filter((id) => id !== tagId);
      onOpenItemsChange(newOpenItems);
    }
  };

  const expandSelected = () => {
    const selectedTag = tagContext?.selectedTag;
    if (!selectedTag) {
      return;
    }

    const selectedChildren = allTags.filter(
      (t) => t.parentId === selectedTag.id
    );

    const newOpenItems = [
      ...openItems,
      ...selectedChildren.map((tag) => tag.id),
      selectedTag.id,
    ];

    onOpenItemsChange(newOpenItems.filter(distinct));
  };

  const closeAll = () => {
    onOpenItemsChange([]);
  };

  const areAllAncestorsOpen = (tag: FlatVsaaTag) => {
    try {
      const ancestors = getAncestors(tag, [], tags);
      let allOpen = true;

      ancestors.forEach((a) => {
        if (!tagIsOpen(a.id)) {
          allOpen = false;
          return;
        }
      });

      return allOpen;
    } catch {
      // getAncestors throws an error if a parent in the chain is not found but here that can be an expected behaviour if one of the grandparents are not open.
      return false;
    }
  };

  const onCreateSave = () => {
    setCreateDialogOpen(false);

    const tagsToOpen: string[] = [];

    if (createItemAncestorIds) {
      createItemAncestorIds.forEach((tagId) => {
        if (!tagIsOpen(tagId)) {
          tagsToOpen.push(tagId);
        }
      });
    }

    const newOpenItems = [...openItems, ...tagsToOpen];
    onOpenItemsChange(newOpenItems);
  };

  const getTreeNodes = () => {
    return tags.map(getTreeNode);
  };

  const onOpenCreateDialog = (parentTag?: FlatVsaaTag) => {
    const targetAncestorIds = [];

    if (parentTag?.ancestorIds) {
      targetAncestorIds.push(...parentTag.ancestorIds);
    }

    if (parentTag) {
      targetAncestorIds.push(parentTag.id);
    }

    const dialogTitle = parentTag
      ? `${t("createTag")} ${parentTag.title}`
      : t("createTagUnder");

    setCreateItemAncestorIds(targetAncestorIds);
    setCreateDialogTitle(dialogTitle);
    setCreateDialogOpen(true);
  };

  const getTreeNode = (tag: FlatVsaaTag) => {
    if (!areAllAncestorsOpen(tag)) {
      return;
    }
    const siblings = tag.siblings ? tag.siblings : 1;
    const siblingsWithCreateBtn = siblings + 1;
    const position = tag.position ? tag.position : 1;
    const level = tag.level ? tag.level : 1;

    return (
      <TagTreeItem
        className={
          tagContext?.selectedTag?.id == tag.id ? classes.selectedTag : ""
        }
        key={tag.id}
        tag={tag}
        tagCollectionId={tagCollectionId}
        parentValue={tag.parentId}
        value={tag.id}
        aria-level={level}
        aria-setsize={siblingsWithCreateBtn}
        aria-posinset={position}
        aria-hidden={false}
        tabIndex={0}
        open={tagIsOpen(tag.id)}
        itemType={tag.hasChildren ? "branch" : "leaf"}
        onClickCreate={onOpenCreateDialog}
        onClick={() => handleSelectedTag(tag)}
      >
        {tag.title}
      </TagTreeItem>
    );
  };

  return (
    <>
      <div className={classes.tagContainer}>
        <div className={classes.leftTagPanel}>
          <div className={classes.treeHeader}>
            <div className={classes.buttonPanel}>
              <Button
                className={classes.treeHeaderButton}
                icon={<ArrowExpandAll20Regular />}
                onClick={expandSelected}
              >
                {t("openSection")}
              </Button>
              <Button
                className={classes.treeHeaderButton}
                icon={<ArrowCollapseAll20Regular />}
                onClick={closeAll}
              >
                {t("closeAll")}
              </Button>
              {accessLevel === 5 && (
                <>
                  <ExportTagsDialog tagCollectionId={tagCollectionId} />
                  <ImportTagsDialog tagCollectionId={tagCollectionId} />
                </>
              )}
            </div>
          </div>

          {isLoading || overviewPanelIsProcessingTags ? (
            <TagTreeSkeleton />
          ) : (
            <FlatTree
              openItems={openItems}
              onOpenChange={handleOpenChange}
              aria-label={t("treeView")}
              className={classes.tree}
            >
              {getTreeNodes()}
            </FlatTree>
          )}
        </div>
        <div className={classes.rightTagPanel}>
          {tagContext?.selectedTag && (
            <TagOverviewPanel
              tagCollectionId={tagCollectionId}
              tags={tags}
              allTags={allTags}
              onProcessingTags={setOverviewPanelIsProcessingTags}
            />
          )}
        </div>
      </div>
      <CreateTreeItemDialog
        key={createDialogOpen ? createDialogTitle : "closed"}
        onClose={() => setCreateDialogOpen(false)}
        onSave={onCreateSave}
        tagCollectionId={tagCollectionId}
        title={createDialogTitle}
        open={createDialogOpen}
        ancestorIds={createItemAncestorIds}
        allTags={allTags}
      />
    </>
  );
};
